import { defineStore } from "pinia";
import { dataService } from "@thegetty-private/rad-app-utils";
import { getClassifiedAs } from "@thegetty/linkedart.js";
import { findAndGetTGNValue } from "@thegetty-private/linked-data.js";
import * as configHelper from "@/helpers/configHelper";

import aat from "@/data/aat.json";

// eslint-disable-next-line no-unused-vars
let dataUrl = configHelper.getDataUrl("/");

const politicalSubdivisions = [
  aat.INHABITED_PLACE,
  aat.FIRST_LEVEL_POLITICAL_SUBDIVISIONS,
  aat.SECOND_LEVEL_POLITICAL_SUBDIVISIONS,
  aat.PRIMARY_POLITICAL_ENTITIES
];

/**
 * allow the overwriting of the data url (testing)
 *
 * @param {string} url
 */
export function setDataUrl(url) {
  dataUrl = url;
}

/**
 * Takes a TGN location and returns all the preferred parents that include
 * one of our desired political sub-divisions.
 *
 * @param {object} location - the LOD for a TGN location
 *
 * @returns {array} - an array of TGN location objects
 */
async function getAllGeographicParents(location, tgnLocations = []) {
  let parentStub = getClassifiedAs(location.part_of, aat.PREFERRED_PARENT)[0];
  let result;

  try {
    result = await dataService.getVocabObject(parentStub.id, "TGN");
  } catch {
    console.error(err);
    return;
  }

  let politicalSubdivision = getPoliticalSubdivision(result.data);

  if (result && politicalSubdivision.length) {
    // skip SECOND_LEVEL_POLITICAL_SUBDIVISIONS. use INHABITED_PLACE,
    // FIRST_LEVEL_POLITICAL_SUBDIVISIONS, and PRIMARY_POLITICAL_ENTITIES
    if (politicalSubdivision[0].id != aat.SECOND_LEVEL_POLITICAL_SUBDIVISIONS) {
      tgnLocations.push(result.data);
    }
    return getAllGeographicParents(result.data, tgnLocations);
  } else {
    return tgnLocations;
  }
}

/**
 * Takes a TGN location and returns the types of political subdivisions.
 *
 * @param {object} location - the LOD for a TGN location
 *
 * @returns {array} - an array of AAT subdivisions
 */
function getPoliticalSubdivision(locationLod) {
  let divisions = politicalSubdivisions
    .map((classification) => {
      return locationLod.classified_as.find((type) => {
        return type.id == classification;
      });
    })
    .flat();

  return divisions.filter((type) => {
    return type !== undefined;
  });
}

/**
 * Takes a TGN location and returns the preferred term.
 *
 * @param {object} location - the LOD for a TGN location
 * @param {string} lang - the language for the location string
 *
 * @returns {string} - the location name
 */
function getLocationName(locationLod, lang = "en") {
  let tgnValues = findAndGetTGNValue({
    id: locationLod.id,
    qualifiers: [locationLod]
  });

  let locationName = Object.values(tgnValues.preferred_name)[0];
  // use preferred_name if matches lang
  // or do we have an option in the preferred lang?
  if (Object.keys(tgnValues.preferred_name).includes(lang)) {
    return locationName;
  } else if (Object.keys(tgnValues.names).includes(lang)) {
    return tgnValues.names[lang][0];
  }
  // otherwise just use the preferred name
  return locationName;
}

export const useTgnStore = defineStore("tgn", {
  state: () => ({
    items: {}
  }),
  actions: {
    async addTgn(id) {
      id = dataService.constructVocabUrl(id, "TGN");

      if (this.items[id] != undefined) {
        return;
      }
      let result;
      let locationLod;
      try {
        result = await dataService.getVocabObject(id, "TGN");
      } catch (err) {
        console.error(err);
        return;
      }

      if (result) {
        locationLod = result.data;
        this.items[id] = { id: id };
      }

      const ref = this.items[id];
      ref.name = getLocationName(locationLod);

      // if the location is a political subdivision add it to the
      // tgnLocations to include it in the fullName. we skip adding
      // it sometimes because the location itself is NOT
      // a political subdivision, but the parent IS the political
      // representation of the inhabited place. For example, the location
      // could be Lexington (classified as an inhabited place) and the
      // parent could also be Lexington (classified as the political
      // subdivision)
      let tgnLocations = [];
      if (getPoliticalSubdivision(locationLod).length) {
        tgnLocations.push(ref);
      }

      if (locationLod.part_of) {
        let parentStub = getClassifiedAs(
          locationLod.part_of,
          aat.PREFERRED_PARENTS
        )[0];
        let parentStubId = parentStub
          ? dataService.constructVocabUrl(parentStub.id, "TGN")
          : undefined;

        if (this.items[parentStubId]) {
          return;
        } else {
          let parentLocations = await getAllGeographicParents(locationLod);

          parentLocations.forEach((location) => {
            this.items[location.id] = { id: location.id };
            this.items[location.id].name = getLocationName(location);
            tgnLocations.push(this.items[location.id]);
          });
        }

        ref.fullName = tgnLocations
          .map((location) => {
            return location.name;
          })
          .join(", ");
      } else {
        ref.fullName = ref.name;
      }
    }
  },
  getters: {
    getTgn(state) {
      return (id) => {
        id = dataService.constructVocabUrl(id, "TGN");
        return state.items[id];
      };
    }
  }
});
